// Twitch cheermote support for the client-side Twitch path. A cheer message
// carries a `bits` IRC tag and words like `Cheer100` / `DoodleCheer250` in its
// body; those words are NOT in the emotes tag, so they arrive as plain text. This
// loads the channel's cheermote map (via the /api/twitch/cheermotes function) and
// turns those words into `cheermote` segments the renderer already draws (animated
// image + colored bit total). Mirrors emoteMap.ts (loadEmotes / applyEmotes).

import type { MessageSegment } from './overlayMessage';

/** prefix (lowercased) → tiers ascending by minBits. */
export type CheermoteMap = Record<string, { minBits: number; color: string; url: string }[]>;

export async function loadCheermotes(roomId: string): Promise<CheermoteMap> {
  try {
    const res = await fetch(`/api/twitch/cheermotes?room=${encodeURIComponent(roomId)}`);
    if (!res.ok) return {};
    const data = (await res.json()) as { cheermotes?: CheermoteMap };
    return data.cheermotes ?? {};
  } catch {
    return {};
  }
}

// A cheermote word is a known prefix immediately followed by a bit amount. Twitch
// prefixes are ALPHANUMERIC and the digits can sit ANYWHERE in them: a channel's own
// prefix comes from its name (`mathox1Cheer`), and Twitch's own globals include
// `4Head`, which starts with one. No letters-then-digits split can represent either,
// so this regex is only a cheap reject — any alphanumeric word ending in a digit and
// containing at least one letter — and the real work is a LONGEST-match lookup against
// the loaded prefix map. Longest wins because real prefixes nest: `cheerwhal` extends
// `cheer`, and a shorter match would leave `whal100` as the amount.
const CHEER_CANDIDATE = /^(?=.*[a-zA-Z])[a-zA-Z0-9]*\d$/;

function resolveCheer(word: string, map: CheermoteMap): { prefix: string; bits: number } | null {
  if (!CHEER_CANDIDATE.test(word)) return null;
  const lower = word.toLowerCase();
  let best: { prefix: string; bits: number } | null = null;
  for (const prefix of Object.keys(map)) {
    if (!lower.startsWith(prefix)) continue;
    const rest = word.slice(prefix.length);
    if (!/^\d+$/.test(rest)) continue;
    if (!best || prefix.length > best.prefix.length) best = { prefix, bits: Number(rest) };
  }
  return best;
}

/** Replace `<prefix><amount>` words in text segments with cheermote segments,
 *  leaving whitespace and non-cheer words intact. */
export function applyCheermotes(segments: MessageSegment[], map: CheermoteMap): MessageSegment[] {
  const out: MessageSegment[] = [];
  for (const seg of segments) {
    if (seg.type !== 'text') {
      out.push(seg);
      continue;
    }
    // Keep the whitespace runs (capture group) so spacing survives the split.
    const parts = seg.content.split(/(\s+)/);
    let buffer = '';
    for (const part of parts) {
      const hit = resolveCheer(part, map);
      const tiers = hit ? map[hit.prefix] : undefined;
      if (hit && tiers && tiers.length) {
        const n = hit.bits;
        // Highest tier whose minBits <= n (tiers are sorted ascending).
        let tier = tiers[0];
        for (const t of tiers) {
          if (t.minBits <= n) tier = t;
          else break;
        }
        if (buffer) {
          out.push({ type: 'text', content: buffer });
          buffer = '';
        }
        out.push({
          type: 'cheermote',
          content: part,
          prefix: hit.prefix,
          bits: n,
          tier: String(tier.minBits),
          color: tier.color,
          cheermote_url: tier.url,
        });
      } else {
        buffer += part;
      }
    }
    if (buffer) out.push({ type: 'text', content: buffer });
  }
  return out;
}
